import React from "react";

import { useSelector } from "react-redux";

import {
  SUMMARY,
  TOTAL,
  EMPTY_CART,
  TRANSACTION_FEE,
  TAX,
  MEMBERSHIP_SUBSCRIPTION,
  DISCOUNT,
} from "../../utils/strings";
import AmenitiesIconList from "../AmenitiesIconList";
import Line from "../Line";
import { Address as AddressImage } from "../../assets/images";
import {
  Container,
  Title,
  SubContainer,
  Subtitle,
  Description,
  AmenitiesContainer,
  Address,
  SocialIconsContainer,
  SocialIconRef,
  SocialIcon,
  TilePurchaseBox,
  Tile,
  ValueTile,
  AddressIcon,
  AddressContainer,
} from "./styles";

const SummaryCard = () => {
  const { basketProduct, selectedProduct, businessProfile, appliedDiscount } = useSelector(
    (state: RootState) => state.appData
  );

  const { name, description, amenities, address, socialLinks }: any =
    (basketProduct || businessProfile) ?? {
      name: "",
      description: "",
      amenities: [],
      address: { fullAddress: "" },
      socialLinks: [],
      products: [],
    };

  const { price } = selectedProduct || {};
  const { feeAmount = 0, preTaxCost = 0 } = price || {};

  const GYM_PRICING_MAP: Record<string, number> = {
    "Elev8tion Fitness Midtown": 188,
    "Legacy Wynwood (Classes Only)": 380,
    "Omega Wellness Club": 289,
    "Purepower Cycle Midtown Miami": 285,
    "UFC GYM Midtown": 129,
    "Gotham Gym Miami": 200,
    "Rumble Boxing South Beach": 189,
    "Rumble Boxing Coral Gables": 189,
    "Prestige Miami Fitness Club": 115,
    "BPM Wellness": 249,
    "Bloom Yoga": 180,
    "AIRLAB Fitness": 255,
    "Legacy Coral Gables (Classes Only)": 359,
    "Lion Heart Boxing Gym Coral Gables": 245,
    "Cycle Bar Fort Lauderdale": 189,
    "SQLPT Pilates": 320,
    "MC Fitness Performance & Training Fort Lauderdale": 179,
    "Symmetry Fitness & Performance": 250,
    "HOTWORX Fort Lauderdale (Flagler Village)": 87,
    "Legacy Fort Lauderdale (Classes Only)": 379,
    "Rise Athletics & Wellness": 299,
    "Powerhouse Gym & Cycleward Studio Fort Lauderdale": 389,
    "RULE Fitness Miami": 133,
    "Raw Fit Miami": 250,
    "ADAPT": 125,
    "Riptide CrossFit": 235,
    "Elev8tion North Miami": 148,
    "CrossFit Wynwood": 200,
    "Glute Station- South Tampa": 200,
    "Optimum Gym": 200,
    "TRIM": 200,
    "UFC GYM Pinellas Park": 234,
    "Glute Station- Channelside": 200,
    "Last Round": 200,
    "Revive Pilates": 449,
    "Ima Yoga": 175,
    "Powerhouse Gym Miami": 150,
    "Palmi + Sano": 200
  };

// === Tax rate: use taxRateBps from product, fall back to address-based rate ===
const fullAddressRaw = address?.fullAddress ?? "";
const fullAddress = fullAddressRaw.replace(/\u00A0/g, " ").trim();

const taxRateBps = selectedProduct?.taxRateBps;
let dynamicTaxRate: number;
if (typeof taxRateBps === 'number' && taxRateBps >= 0) {
  dynamicTaxRate = taxRateBps / 10_000;
} else {
  const parts = fullAddress.split(",");
  const citySegment = (parts[1] ?? "").toLowerCase().trim();
  const isTampa = citySegment === "tampa" || /\btampa\b/i.test(fullAddress);
  dynamicTaxRate = isTampa ? 0.075 : 0.07;
}

// Compute tax and fee on full preTaxCost (no discount before tax)
const FEE_RATE = 0.035; // 3.5%
const baseCents = Math.round(preTaxCost * 100);
const computedTaxAmount = Number((preTaxCost * dynamicTaxRate).toFixed(2));
const taxCents = Math.round(computedTaxAmount * 100);
const feeCents = Math.round((baseCents + taxCents) * FEE_RATE);
const computedFeeAmount = feeCents / 100;
const totalBeforeDiscountCents = baseCents + taxCents + feeCents;
const totalBeforeDiscount = totalBeforeDiscountCents / 100;

// Apply discount to total after tax + fees (matching mobile app behavior)
let discountAmount = 0;
if (appliedDiscount && selectedProduct?.price) {
  if (appliedDiscount.type === 'percentage') {
    discountAmount = Number(((totalBeforeDiscount * appliedDiscount.amount) / 100).toFixed(2));
  } else {
    discountAmount = Number(Math.min(appliedDiscount.amount, totalBeforeDiscount).toFixed(2));
  }
}

const finalTotal = Number(Math.max(0, totalBeforeDiscount - discountAmount).toFixed(2));


  const originalPrice = GYM_PRICING_MAP[name] || null;
  const jackedRabbitPrice = preTaxCost || 75; // default fallback
  const savingsAmount = originalPrice ? originalPrice - jackedRabbitPrice : 0;
  const savingsPercent = originalPrice
    ? Math.round((savingsAmount / originalPrice) * 100)
    : 0;

  return (
    <Container>
      <Title> {SUMMARY}</Title>
      <SubContainer>
        {basketProduct || selectedProduct ? (
          <>
            <Subtitle>{name}</Subtitle>
            <Description>{description}</Description>
            <AmenitiesContainer>
              <AmenitiesIconList amenities={amenities} />
            </AmenitiesContainer>
            <AddressContainer>
              <AddressIcon src={AddressImage} />
              {address && (
                <Address
                  href={`http://maps.google.com/?q=${address?.fullAddress}`}
                  target="_blank"
                  rel="noopener"
                >
                  {address?.fullAddress}
                </Address>
              )}
            </AddressContainer>
            <SocialIconsContainer>
              {socialLinks?.map(({ name, socialLink }: any) => {
                return (
                  name &&
                  socialLink && (
                    <SocialIconRef
                      href={socialLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      key={name}
                    >
                      <SocialIcon
                        src={require(`../../assets/images/${name}.svg`)}
                      />
                    </SocialIconRef>
                  )
                );
              })}
            </SocialIconsContainer>
            {originalPrice && (
              <TilePurchaseBox>
                <Tile style={{ fontSize: "13px", color: "#ccc" }}>
                  Normally up to ${originalPrice}/month
                </Tile>
                <ValueTile style={{ color: "#4CAF50", fontSize: "14px" }}>
                  Save up to ${savingsAmount} ({savingsPercent}%)
                </ValueTile>
              </TilePurchaseBox>
            )}

            <Line />
            <TilePurchaseBox>
              <Tile>{MEMBERSHIP_SUBSCRIPTION}</Tile>
              {preTaxCost !== undefined && <ValueTile>$ {preTaxCost.toFixed ? preTaxCost.toFixed(2) : Number(preTaxCost).toFixed(2)}</ValueTile>}
            </TilePurchaseBox>

            <TilePurchaseBox>
              <Tile>Tax ({(dynamicTaxRate * 100).toFixed(2)}%):</Tile>
              <ValueTile>$ {computedTaxAmount.toFixed(2)}</ValueTile>
            </TilePurchaseBox>
            <TilePurchaseBox>
              <Tile>{TRANSACTION_FEE}</Tile>
              <ValueTile>$ {computedFeeAmount.toFixed(2)}</ValueTile>
            </TilePurchaseBox>

            {appliedDiscount && (
              <TilePurchaseBox>
                <Tile>{DISCOUNT} ({appliedDiscount.code})</Tile>
                <ValueTile style={{ color: '#4CAF50' }}>
                  -$ {discountAmount.toFixed(2)}
                </ValueTile>
              </TilePurchaseBox>
            )}

            <Line />
            <TilePurchaseBox>
              <Tile>{TOTAL}</Tile>
              <ValueTile>$ {finalTotal.toFixed(2)}</ValueTile>
            </TilePurchaseBox>

            <div style={{ marginTop: '14px', padding: '12px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
              <p style={{ fontSize: '12px', color: '#555', lineHeight: '1.7', margin: 0 }}>
                After your first month, your membership continues at the rate of your chosen renewal plan at <strong>{name}</strong>, plus applicable taxes and fees. You can select a new trial or cancel anytime.
              </p>
            </div>
          </>
        ) : (
          <Subtitle>{EMPTY_CART}</Subtitle>
        )}
      </SubContainer>
    </Container>
  );
};

export default SummaryCard;
